import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Package, Truck, Activity, MapPin,
  CheckCircle, AlertTriangle, Shield, BarChart3, Calendar
} from 'lucide-react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const THRDistrictDetailModal = ({ district, isOpen, onClose, reportingPeriod, dashboardType = 'thr' }) => {
  const [districtData, setDistrictData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen && district) {
      console.log('Modal opened with district:', district);
      console.log('Dashboard type:', dashboardType);
      fetchDistrictDetails();
    } else {
      // Reset data when modal closes
      setDistrictData(null);
      setLoading(false);
    }
  }, [isOpen, district, reportingPeriod, dashboardType]);

  const fetchDistrictDetails = async () => {
    // Handle both district.district and district.district_id formats
    let districtId = district?.district || district?.district_id;
    if (!districtId || districtId.trim() === '') {
      console.error('District ID is missing or empty');
      setDistrictData(null);
      return;
    }
    
    // Normalize district name: Convert to Title Case for API compatibility
    // Handle cases like "SURENDRANAGAR" -> "Surendranagar", "SURAT RURAL" -> "Surat Rural"
    districtId = districtId.trim();
    if (districtId === districtId.toUpperCase() && districtId.length > 0) {
      // If all caps, convert to Title Case (handles multi-word names)
      districtId = districtId.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    } else if (districtId.length > 0) {
      // If mixed case, ensure first letter of each word is uppercase
      districtId = districtId.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    
    console.log('Original district ID:', district?.district || district?.district_id);
    console.log('Normalized district ID:', districtId);
    
    setLoading(true);
    let endpoint;
    try {
      if (dashboardType === 'thr') {
        endpoint = `${API}/api/dashboards/thr/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'hcm') {
        endpoint = `${API}/api/dashboards/hcm-enhanced/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'frs') {
        endpoint = `${API}/api/dashboards/frs-auth/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'growth-monitoring') {
        endpoint = `${API}/api/dashboards/growth-monitoring/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'enrollment-dropout') {
        endpoint = `${API}/api/dashboards/enrollment-dropout/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'infrastructure') {
        endpoint = `${API}/api/dashboards/infrastructure/district-details/${encodeURIComponent(districtId)}`;
      } else if (dashboardType === 'purna') {
        endpoint = `${API}/api/dashboards/purna/district-details/${encodeURIComponent(districtId)}`;
      } else {
        // Default to THR if dashboardType is not recognized
        endpoint = `${API}/api/dashboards/thr/district-details/${encodeURIComponent(districtId)}`;
      }
      
      if (!endpoint) {
        console.error('Endpoint is not defined');
        setLoading(false);
        return;
      }
      
      const params = {};
      if (reportingPeriod) {
        params.reporting_period = reportingPeriod;
      }
      
      const response = await axios.get(endpoint, { params });
      
      console.log('API Response:', response.data);
      console.log('Response status:', response.status);
      
      if (response.data && Object.keys(response.data).length > 0) {
        setDistrictData(response.data);
      } else {
        console.error('No data received from API or empty response');
        setDistrictData(null);
      }
    } catch (error) {
      console.error('Error fetching district details:', error);
      console.error('District ID:', districtId);
      console.error('Endpoint:', endpoint);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      setDistrictData(null);
    } finally {
      setLoading(false);
    }
  };

  const getPerformanceColor = (pct) => {
    if (pct >= 80) return 'text-green-600';
    if (pct >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBadge = (performance) => {
    const colors = {
      'Excellent': 'bg-green-100 text-green-800',
      'Good': 'bg-yellow-100 text-yellow-800',
      'Average': 'bg-yellow-100 text-yellow-800',
      'Needs Improvement': 'bg-red-100 text-red-800'
    };
    return colors[performance] || 'bg-gray-100 text-gray-800';
  };

  const COLORS = ['#10b981', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6'];

  if (!isOpen || !district) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-600" />
            {district.district} - {dashboardType === 'thr' ? 'THR' : dashboardType === 'hcm' ? 'HCM' : dashboardType === 'frs' ? 'FRS' : dashboardType === 'growth-monitoring' ? 'Growth Monitoring' : dashboardType === 'enrollment-dropout' ? 'Enrollment & Drop-off' : dashboardType === 'infrastructure' ? 'Infrastructure' : dashboardType === 'purna' ? 'PURNA' : 'District'} District Details
          </DialogTitle>
          <DialogDescription>
            Comprehensive performance analysis and statistics
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading district details...</span>
          </div>
        ) : districtData ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="trends">Trends</TabsTrigger>
              <TabsTrigger value="comparison">Comparison</TabsTrigger>
              <TabsTrigger value="insights">Insights & Actions</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {dashboardType === 'thr' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total AWCs</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.anganwadi_count || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Overall Delivery %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary?.overall_avg_delivery_pct || 0)}`}>
                          {districtData.summary?.overall_avg_delivery_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Balshakti Delivery</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.ration_performance?.balshakti?.delivery_pct || 0)}`}>
                          {districtData.ration_performance?.balshakti?.delivery_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Matru Delivery</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.ration_performance?.matru?.delivery_pct || 0)}`}>
                          {districtData.ration_performance?.matru?.delivery_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : dashboardType === 'frs' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total Eligible</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{(districtData.frs_performance?.total_eligible || 0).toLocaleString()}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Aadhaar Face Match %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.frs_performance?.face_match_pct || 0)}`}>
                          {districtData.frs_performance?.face_match_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">THR-FRS Auth %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.frs_performance?.face_matched_thr_pct || 0)}`}>
                          {districtData.frs_performance?.face_matched_thr_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Kit Productivity</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor((districtData.aadhaar_performance?.enrol_upd_per_kit_per_day || 0) * 10)}`}>
                          {districtData.aadhaar_performance?.enrol_upd_per_kit_per_day?.toFixed(1) || 0}/day
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : dashboardType === 'growth-monitoring' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total Beneficiaries</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.total_beneficiaries || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Coverage %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary?.coverage_pct || 0)}`}>
                          {districtData.summary?.coverage_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Mobile Verified</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.beneficiaries?.mobile_verified_pct || 0)}`}>
                          {districtData.beneficiaries?.mobile_verified_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Aadhaar Verified</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.beneficiaries?.aadhaar_verified_pct || 0)}`}>
                          {districtData.beneficiaries?.aadhaar_verified_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : dashboardType === 'enrollment-dropout' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total Enrolled</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.total_enrolled || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Active %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary?.active_pct || 0)}`}>
                          {districtData.summary?.active_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Drop-off %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(100 - (districtData.summary?.dropoff_pct || 0))}`}>
                          {districtData.summary?.dropoff_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Monitored</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.enrollment_stats?.monitored_pct || 0)}`}>
                          {districtData.enrollment_stats?.monitored_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : dashboardType === 'infrastructure' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total AWCs</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.total_awcs || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Infrastructure Score</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary?.infrastructure_score || 0)}`}>
                          {districtData.summary?.infrastructure_score?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Own Building %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.building_ownership?.own_pct || 0)}`}>
                          {districtData.building_ownership?.own_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Functional Toilets %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.sanitation?.functional_toilet_pct || 0)}`}>
                          {districtData.sanitation?.functional_toilet_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : dashboardType === 'purna' ? (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total Surveyed</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.total_surveyed || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Enrolled</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.total_enrolled || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Enrollment %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.summary?.enrollment_pct || 0)}`}>
                          {districtData.summary?.enrollment_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">SNP Coverage %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.services?.snp_pct || 0)}`}>
                          {districtData.services?.snp_pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Total AWCs</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">{districtData.summary?.awc_count || 0}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Allocated (KG)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {(districtData.supply_chain?.allocation?.qty / 1000).toFixed(1)}K
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Issued (KG)</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {(districtData.supply_chain?.issuance?.qty / 1000).toFixed(1)}K
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-600">Sale %</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${getPerformanceColor(districtData.supply_chain?.sale?.pct || 0)}`}>
                          {districtData.supply_chain?.sale?.pct?.toFixed(1) || 0}%
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>

              {/* Delivery/Supply/FRS Breakdown Chart - Only show for THR, FRS, HCM */}
              {(dashboardType === 'thr' || dashboardType === 'frs' || dashboardType === 'hcm') && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {dashboardType === 'thr' ? 'Delivery Status Breakdown' : 
                       dashboardType === 'frs' ? 'FRS Authentication Breakdown' : 
                       'Supply Chain Breakdown'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={
                            dashboardType === 'thr' ? districtData.delivery_breakdown : 
                            dashboardType === 'frs' ? districtData.frs_breakdown :
                            districtData.supply_breakdown
                          }
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => {
                            // Only show label if percentage is >= 5% to avoid overlapping small slices
                            if (percent * 100 >= 5) {
                              return `${name}: ${(percent * 100).toFixed(0)}%`;
                            }
                            return '';
                          }}
                          outerRadius={100}
                          innerRadius={0}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {(
                            dashboardType === 'thr' ? districtData.delivery_breakdown : 
                            dashboardType === 'frs' ? districtData.frs_breakdown :
                            districtData.supply_breakdown
                          )?.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Enrollment Breakdown Chart */}
              {dashboardType === 'enrollment-dropout' && districtData.enrollment_breakdown && (
                <Card>
                  <CardHeader>
                    <CardTitle>Enrollment Status Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={districtData.enrollment_breakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => {
                            if (percent * 100 >= 5) {
                              return `${name}: ${(percent * 100).toFixed(0)}%`;
                            }
                            return '';
                          }}
                          outerRadius={120}
                          innerRadius={40}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {districtData.enrollment_breakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ fontSize: '12px' }}
                          formatter={(value) => value.toLocaleString()}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                          iconSize={12}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Additional stats for enrollment-dropout */}
              {dashboardType === 'enrollment-dropout' && districtData.enrollment_stats && (
                <Card>
                  <CardHeader>
                    <CardTitle>Enrollment Statistics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Total</p>
                        <p className="text-2xl font-bold">{districtData.enrollment_stats.total?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Active</p>
                        <p className="text-2xl font-bold text-green-600">{districtData.enrollment_stats.active?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Dropped</p>
                        <p className="text-2xl font-bold text-red-600">{districtData.enrollment_stats.dropped?.toLocaleString() || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Monitored</p>
                        <p className="text-2xl font-bold text-blue-600">{districtData.enrollment_stats.monitored?.toLocaleString() || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Infrastructure Breakdown Charts */}
              {dashboardType === 'infrastructure' && (
                <>
                  {districtData.infrastructure_breakdown && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Building Ownership Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <Pie
                              data={districtData.infrastructure_breakdown}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              label={({ name, percent, value }) => {
                                if (percent * 100 >= 5) {
                                  return `${name}: ${value.toLocaleString()}`;
                                }
                                return '';
                              }}
                              outerRadius={120}
                              innerRadius={40}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {districtData.infrastructure_breakdown.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ fontSize: '12px' }}
                              formatter={(value) => value.toLocaleString()}
                            />
                            <Legend 
                              wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                              iconSize={12}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}
                  
                  {districtData.amenities_breakdown && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Sanitation Facilities Distribution</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                            <Pie
                              data={districtData.amenities_breakdown}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              label={({ name, percent, value }) => {
                                if (percent * 100 >= 5) {
                                  return `${name}: ${value.toLocaleString()}`;
                                }
                                return '';
                              }}
                              outerRadius={120}
                              innerRadius={40}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {districtData.amenities_breakdown.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ fontSize: '12px' }}
                              formatter={(value) => value.toLocaleString()}
                            />
                            <Legend 
                              wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                              iconSize={12}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle>Infrastructure Details</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Own Building %</p>
                          <p className="text-2xl font-bold text-green-600">{districtData.building_ownership?.own_pct?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Functional Toilets %</p>
                          <p className="text-2xl font-bold text-blue-600">{districtData.sanitation?.functional_toilet_pct?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Electricity %</p>
                          <p className="text-2xl font-bold text-yellow-600">{districtData.electricity?.electricity_pct?.toFixed(1) || 0}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600">Dilapidated %</p>
                          <p className="text-2xl font-bold text-red-600">{districtData.dilapidation?.dilapidated_pct?.toFixed(1) || 0}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {/* PURNA Enrollment Breakdown Chart */}
              {dashboardType === 'purna' && districtData.enrollment_breakdown && (
                <Card>
                  <CardHeader>
                    <CardTitle>Enrollment Status Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={districtData.enrollment_breakdown}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => {
                            if (percent * 100 >= 5) {
                              return `${name}: ${(percent * 100).toFixed(0)}%`;
                            }
                            return '';
                          }}
                          outerRadius={120}
                          innerRadius={40}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {districtData.enrollment_breakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ fontSize: '12px' }}
                          formatter={(value) => value.toLocaleString()}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                          iconSize={12}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* PURNA Service Coverage Chart */}
              {dashboardType === 'purna' && districtData.service_breakdown && (
                <Card>
                  <CardHeader>
                    <CardTitle>Service Coverage Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={districtData.service_breakdown}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          width={80}
                        />
                        <Tooltip 
                          contentStyle={{ fontSize: '12px' }}
                          formatter={(value) => value.toLocaleString()}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }}
                          iconSize={12}
                        />
                        <Bar dataKey="value" fill="#8b5cf6" name="Girls">
                          {districtData.service_breakdown.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {dashboardType === 'purna' && districtData.services && (
                <Card>
                  <CardHeader>
                    <CardTitle>Service Coverage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">SNP Coverage</p>
                        <p className="text-2xl font-bold text-purple-600">{districtData.services.snp_pct?.toFixed(1) || 0}%</p>
                        <p className="text-xs text-gray-500">{districtData.services.snp_received?.toLocaleString() || 0} girls</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">IFA Coverage</p>
                        <p className="text-2xl font-bold text-blue-600">{districtData.services.ifa_pct?.toFixed(1) || 0}%</p>
                        <p className="text-xs text-gray-500">{districtData.services.ifa_received?.toLocaleString() || 0} girls</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Health Checkup</p>
                        <p className="text-2xl font-bold text-green-600">{districtData.services.health_checkup_pct?.toFixed(1) || 0}%</p>
                        <p className="text-xs text-gray-500">{districtData.services.health_checkup?.toLocaleString() || 0} girls</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-4">
              {dashboardType === 'enrollment-dropout' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Enrollment Performance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Enrolled:</span>
                        <span className="font-semibold">{(districtData.summary?.total_enrolled || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active Beneficiaries:</span>
                        <span className="font-semibold text-green-600">{(districtData.summary?.active_beneficiaries || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.summary?.active_pct || 0)}`}>
                          {districtData.summary?.active_pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Drop-off %:</span>
                        <span className={`font-bold ${getPerformanceColor(100 - (districtData.summary?.dropoff_pct || 0))}`}>
                          {districtData.summary?.dropoff_pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                      {districtData.enrollment_stats && (
                        <>
                          <div className="flex justify-between pt-2 border-t">
                            <span className="text-gray-600">Monitored:</span>
                            <span className="font-semibold">{(districtData.enrollment_stats.monitored || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Monitored %:</span>
                            <span className={`font-bold ${getPerformanceColor(districtData.enrollment_stats.monitored_pct || 0)}`}>
                              {districtData.enrollment_stats.monitored_pct?.toFixed(1) || 0}%
                            </span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  {districtData.verification && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Verification Status</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mobile Verified:</span>
                          <span className="font-semibold text-green-600">{(districtData.verification.mobile_verified || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mobile Verified %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.verification.mobile_verified_pct || 0)}`}>
                            {districtData.verification.mobile_verified_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-600">Aadhaar Verified:</span>
                          <span className="font-semibold text-blue-600">{(districtData.verification.aadhaar_verified || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Aadhaar Verified %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.verification.aadhaar_verified_pct || 0)}`}>
                            {districtData.verification.aadhaar_verified_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : dashboardType === 'infrastructure' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Building Ownership</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total AWCs:</span>
                        <span className="font-semibold">{(districtData.summary?.total_awcs || 0).toLocaleString()}</span>
                      </div>
                      {districtData.building_ownership && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Own Building %:</span>
                            <span className={`font-bold ${getPerformanceColor(districtData.building_ownership.own_pct || 0)}`}>
                              {districtData.building_ownership.own_pct?.toFixed(1) || 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Rented %:</span>
                            <span className="font-semibold">{districtData.building_ownership.rented_pct?.toFixed(1) || 0}%</span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Amenities & Quality</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {districtData.sanitation && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Functional Toilets %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.sanitation.functional_toilet_pct || 0)}`}>
                            {districtData.sanitation.functional_toilet_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      )}
                      {districtData.electricity && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Electricity %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.electricity.electricity_pct || 0)}`}>
                            {districtData.electricity.electricity_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      )}
                      {districtData.dilapidation && (
                        <>
                          <div className="flex justify-between pt-2 border-t">
                            <span className="text-gray-600">Dilapidated %:</span>
                            <span className={`font-bold ${getPerformanceColor(100 - (districtData.dilapidation.dilapidated_pct || 0))}`}>
                              {districtData.dilapidation.dilapidated_pct?.toFixed(1) || 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Infrastructure Score:</span>
                            <span className={`font-bold ${getPerformanceColor(districtData.summary?.infrastructure_score || 0)}`}>
                              {districtData.summary?.infrastructure_score?.toFixed(1) || 0}
                            </span>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : dashboardType === 'purna' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Enrollment Performance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Surveyed:</span>
                        <span className="font-semibold">{(districtData.summary?.total_surveyed || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Enrolled:</span>
                        <span className="font-semibold text-green-600">{(districtData.summary?.total_enrolled || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Enrollment %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.summary?.enrollment_pct || 0)}`}>
                          {districtData.summary?.enrollment_pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  {districtData.services && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Service Coverage</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">SNP Coverage %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.services.snp_pct || 0)}`}>
                            {districtData.services.snp_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">IFA Coverage %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.services.ifa_pct || 0)}`}>
                            {districtData.services.ifa_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Health Checkup %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.services.health_checkup_pct || 0)}`}>
                            {districtData.services.health_checkup_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {districtData.nutrition && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Nutrition Status</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">HB Tested:</span>
                          <span className="font-semibold">{(districtData.nutrition.hb_tested || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">HB Tested %:</span>
                          <span className={`font-bold ${getPerformanceColor(districtData.nutrition.hb_tested_pct || 0)}`}>
                            {districtData.nutrition.hb_tested_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Anemia %:</span>
                          <span className={`font-bold ${getPerformanceColor(100 - (districtData.nutrition.anemia_pct || 0))}`}>
                            {districtData.nutrition.anemia_pct?.toFixed(1) || 0}%
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : dashboardType === 'frs' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>FRS Performance Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Eligible:</span>
                        <span className="font-semibold">{(districtData.frs_performance?.total_eligible || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Aadhaar Face Matched:</span>
                        <span className="font-semibold text-green-600">{(districtData.frs_performance?.aadhaar_face_matched || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Face Match %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.frs_performance?.face_match_pct || 0)}`}>
                          {districtData.frs_performance?.face_match_pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Face Captured THR:</span>
                        <span className="font-semibold">{(districtData.frs_performance?.face_captured_thr || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Face Captured THR %:</span>
                        <span className="font-semibold">{districtData.frs_performance?.face_captured_thr_pct?.toFixed(1) || 0}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Face Matched THR:</span>
                        <span className="font-semibold text-blue-600">{(districtData.frs_performance?.face_matched_thr || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t">
                        <span className="text-gray-600 font-bold">THR-FRS Auth %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.frs_performance?.face_matched_thr_pct || 0)}`}>
                          {districtData.frs_performance?.face_matched_thr_pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  {districtData.aadhaar_performance && Object.keys(districtData.aadhaar_performance).length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Aadhaar Kit Performance</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Allocated Kits:</span>
                          <span className="font-semibold">{districtData.aadhaar_performance.allocated_kits || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Active Kits:</span>
                          <span className="font-semibold text-green-600">{districtData.aadhaar_performance.active_kits || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Enrol/Upd:</span>
                          <span className="font-semibold">{(districtData.aadhaar_performance.total_enrol_upd || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Updation Count:</span>
                          <span className="font-semibold">{(districtData.aadhaar_performance.updation_count || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">New Enrollment:</span>
                          <span className="font-semibold text-blue-600">{(districtData.aadhaar_performance.new_enrollment_count || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-600 font-bold">Productivity:</span>
                          <span className={`font-bold ${getPerformanceColor((districtData.aadhaar_performance.enrol_upd_per_kit_per_day || 0) * 10)}`}>
                            {districtData.aadhaar_performance.enrol_upd_per_kit_per_day?.toFixed(1) || 0} ops/day/kit
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : dashboardType === 'thr' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {['balshakti', 'matru', 'purna'].map((ration) => {
                    const data = districtData.ration_performance?.[ration];
                    return (
                      <Card key={ration}>
                        <CardHeader>
                          <CardTitle className="capitalize">{ration === 'balshakti' ? 'Balshakti (500mg)' : ration === 'matru' ? 'Matru Shakti (1kg)' : 'Purna Shakti (1kg)'}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Scheduled:</span>
                            <span className="font-semibold">{data?.scheduled || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivered:</span>
                            <span className="font-semibold text-green-600">{data?.delivered || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery %:</span>
                            <span className={`font-bold ${getPerformanceColor(data?.delivery_pct || 0)}`}>
                              {data?.delivery_pct?.toFixed(1) || 0}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">OTP %:</span>
                            <span className="font-semibold">{data?.otp_pct?.toFixed(1) || 0}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Gap:</span>
                            <span className="font-semibold text-red-600">{data?.gap || 0}</span>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Allocation Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Allocated:</span>
                        <span className="font-semibold">{(districtData.supply_chain?.allocation?.qty / 1000).toFixed(1)}K KG</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Per AWC:</span>
                        <span className="font-semibold">{districtData.supply_chain?.allocation?.per_awc?.toFixed(1)} KG</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Issuance Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Issued:</span>
                        <span className="font-semibold">{(districtData.supply_chain?.issuance?.qty / 1000).toFixed(1)}K KG</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Issue %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.supply_chain?.issuance?.pct || 0)}`}>
                          {districtData.supply_chain?.issuance?.pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Status:</span>
                        <Badge className={districtData.supply_chain?.issuance?.has_started ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {districtData.supply_chain?.issuance?.has_started ? 'Started' : 'Not Started'}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Sale Metrics</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Sale:</span>
                        <span className="font-semibold">{(districtData.supply_chain?.sale?.qty / 1000).toFixed(1)}K KG</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sale %:</span>
                        <span className={`font-bold ${getPerformanceColor(districtData.supply_chain?.sale?.pct || 0)}`}>
                          {districtData.supply_chain?.sale?.pct?.toFixed(1) || 0}%
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Stock Status</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Stock at Risk:</span>
                        <span className="font-semibold text-red-600">{(districtData.supply_chain?.stock_at_risk / 1000).toFixed(1)}K KG</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* Trends Tab */}
            <TabsContent value="trends" className="space-y-4">
              {districtData.time_series && districtData.time_series.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Trends Over Time</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart 
                        data={districtData.time_series}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="period" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          width={60}
                        />
                        <Tooltip />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconSize={12}
                          style={{ fontSize: '12px' }}
                        />
                        {dashboardType === 'thr' ? (
                          <>
                            <Line type="monotone" dataKey="balshakti_delivery_pct" stroke="#3b82f6" name="Balshakti %" />
                            <Line type="monotone" dataKey="matru_delivery_pct" stroke="#f59e0b" name="Matru %" />
                            <Line type="monotone" dataKey="purna_delivery_pct" stroke="#22c55e" name="Purna %" />
                            <Line type="monotone" dataKey="avg_delivery_pct" stroke="#8b5cf6" name="Average %" strokeWidth={2} />
                          </>
                        ) : (
                          <>
                            <Line type="monotone" dataKey="allocated_qty" stroke="#3b82f6" name="Allocated (KG)" />
                            <Line type="monotone" dataKey="issued_qty" stroke="#f59e0b" name="Issued (KG)" />
                            <Line type="monotone" dataKey="sale_qty" stroke="#22c55e" name="Sale (KG)" />
                          </>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-center py-8">Trend data not available for this district.</p>
                  </CardContent>
                </Card>
              )}

              {districtData.trends && Object.keys(districtData.trends).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(districtData.trends).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          {value >= 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <div className="text-sm text-gray-600 capitalize">{key.replace(/_/g, ' ')}</div>
                            <div className={`text-lg font-bold ${value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {value >= 0 ? '+' : ''}{typeof value === 'number' ? value.toFixed(2) : value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Comparison Tab */}
            <TabsContent value="comparison" className="space-y-4">
              {districtData.comparison_with_state && Object.keys(districtData.comparison_with_state).length > 0 ? (
                <>
                  <Card>
                    <CardHeader>
                      <CardTitle>Comparison with State Average</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={
                          dashboardType === 'thr' ? [
                            {
                              name: 'Balshakti',
                              district: districtData.comparison_with_state?.balshakti?.district || 0,
                              state: districtData.comparison_with_state?.balshakti?.state_avg || 0
                            },
                            {
                              name: 'Matru',
                              district: districtData.comparison_with_state?.matru?.district || 0,
                              state: districtData.comparison_with_state?.matru?.state_avg || 0
                            },
                            {
                              name: 'Purna',
                              district: districtData.comparison_with_state?.purna?.district || 0,
                              state: districtData.comparison_with_state?.purna?.state_avg || 0
                            }
                          ] : dashboardType === 'frs' ? [
                            {
                              name: 'Face Match %',
                              district: districtData.comparison_with_state?.face_match_pct?.district || 0,
                              state: districtData.comparison_with_state?.face_match_pct?.state_avg || 0
                            },
                            {
                              name: 'Face Captured THR %',
                              district: districtData.comparison_with_state?.face_captured_thr_pct?.district || 0,
                              state: districtData.comparison_with_state?.face_captured_thr_pct?.state_avg || 0
                            },
                            {
                              name: 'THR-FRS Auth %',
                              district: districtData.comparison_with_state?.face_matched_thr_pct?.district || 0,
                              state: districtData.comparison_with_state?.face_matched_thr_pct?.state_avg || 0
                            },
                            ...(districtData.comparison_with_state?.kit_productivity ? [{
                              name: 'Kit Productivity',
                              district: districtData.comparison_with_state?.kit_productivity?.district || 0,
                              state: districtData.comparison_with_state?.kit_productivity?.state_avg || 0
                            }] : [])
                          ] : [
                            {
                              name: 'Allocated (K)',
                              district: (districtData.comparison_with_state?.allocated_qty?.district || 0) / 1000,
                              state: (districtData.comparison_with_state?.allocated_qty?.state_avg || 0) / 1000
                            },
                            {
                              name: 'Issued (K)',
                              district: (districtData.comparison_with_state?.issued_qty?.district || 0) / 1000,
                              state: (districtData.comparison_with_state?.issued_qty?.state_avg || 0) / 1000
                            },
                            {
                              name: 'Sale (K)',
                              district: (districtData.comparison_with_state?.sale_qty?.district || 0) / 1000,
                              state: (districtData.comparison_with_state?.sale_qty?.state_avg || 0) / 1000
                            },
                            {
                              name: 'Issue %',
                              district: districtData.comparison_with_state?.issue_pct?.district || 0,
                              state: districtData.comparison_with_state?.issue_pct?.state_avg || 0
                            },
                            {
                              name: 'Sale %',
                              district: districtData.comparison_with_state?.sale_pct?.district || 0,
                              state: districtData.comparison_with_state?.sale_pct?.state_avg || 0
                            }
                          ]
                        }>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="name" 
                            angle={-45}
                            textAnchor="end"
                            height={80}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            width={60}
                          />
                          <Tooltip />
                          <Legend 
                            wrapperStyle={{ paddingTop: '20px' }}
                            iconSize={12}
                            style={{ fontSize: '12px' }}
                          />
                          <Bar dataKey="district" fill="#3b82f6" name="District" />
                          <Bar dataKey="state" fill="#94a3b8" name="State Average" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Difference from State Average</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(districtData.comparison_with_state).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">District: {typeof value?.district === 'number' ? value.district.toFixed(1) : value?.district}</span>
                              <span className="text-gray-400">|</span>
                              <span className="text-gray-600">State: {typeof value?.state_avg === 'number' ? value.state_avg.toFixed(1) : value?.state_avg}</span>
                              <Badge className={value?.difference >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {value?.difference >= 0 ? '+' : ''}{typeof value?.difference === 'number' ? value.difference.toFixed(1) : value?.difference}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>Comparison with State Average</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-center py-8">Comparison data not available for this district.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Insights & Actions Tab */}
            <TabsContent value="insights" className="space-y-4">
              {districtData.improvement_analysis ? (
                <>
                  {/* Key Insights */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" />
                        Key Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-2">
                        {(districtData.improvement_analysis.insights || []).map((insight, i) => (
                          <li key={i} className="text-sm text-gray-700">{insight}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Recommendations */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-500" />
                        Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-2">
                        {(districtData.improvement_analysis.recommendations || []).map((rec, i) => (
                          <li key={i} className="text-sm text-gray-700">{rec}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  {/* Action Items */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        Action Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {(districtData.improvement_analysis.action_items || []).map((action, i) => (
                          <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <span 
                                  className={`px-2 py-1 text-xs font-medium rounded ${
                                    action.priority === 'High' ? 'bg-red-100 text-red-700' :
                                    action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {action.priority} Priority
                                </span>
                                <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-700">
                                  {action.ration_type || action.category || 'All'}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{action.timeline}</span>
                            </div>
                            <h6 className="text-sm font-semibold text-gray-900 mb-1">{action.action}</h6>
                            <p className="text-xs text-gray-600 mb-2">{action.description}</p>
                            <p className="text-xs text-gray-500">
                              <span className="font-medium">Responsible:</span> {action.responsible}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                    <p>No improvement analysis available for this district.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No data available for this district
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default THRDistrictDetailModal;

